import { createEventLocation } from 'api/event'
import { createLocation, updateLocation } from 'api/locations'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { SeatSellingMode, SeatSellingModes } from 'types/Event'
import { LocationType } from 'types/Location'

import { useSeatManagement } from './useSeatManagement'
import { calculateDimensions, generateSeats } from './utils'

export interface UseLocationFormProps {
    initialData?: LocationType | null
    onClose?: () => void
    queryKey?: string[]
    eventId?: string
    partnerId?: string
    onSuccess?: (event_location_id?: string) => void
}

export const useLocationForm = ({
    initialData,
    onClose,
    queryKey = ['locations'],
    eventId,
    partnerId,
    onSuccess,
}: UseLocationFormProps) => {
    const [defaultSeatSellingMode, setDefaultSeatSellingMode] = useState<SeatSellingMode>(
        initialData?.default_seat_selling_mode || SeatSellingModes.SeatBased,
    )
    const [auditoriumMiddleColumn, setAuditoriumMiddleColumn] = useState<number | undefined>(
        initialData?.auditorium?.middle_column || undefined,
    )
    const [galleryMiddleColumn, setGalleryMiddleColumn] = useState<number | undefined>(
        initialData?.auditorium?.gallery?.middle_column || undefined,
    )
    const defaultColor = initialData?.color?.startsWith('#') ? initialData.color.slice(1) : initialData?.color || 'ba9d31'

    const sourceOfChange = useRef<'rowsColumns' | 'totalSeats' | null>(null)
    const lastEventLocationIdRef = useRef<string | undefined>(undefined)

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useForm({
        defaultValues: {
            locationName: initialData?.name || '',
            address: initialData?.address || '',
            totalSeats: initialData?.auditorium?.seats.length || 0,
            rows: initialData?.auditorium?.rows || null,
            columns: initialData?.auditorium?.columns || null,
            galleryRows: initialData?.auditorium?.gallery?.rows || null,
            galleryColumns: initialData?.auditorium?.gallery?.columns || null,
            galleryName: initialData?.auditorium?.gallery?.gallery_name || 'Karzat',
            defaultSeatSellingMode: initialData?.default_seat_selling_mode || SeatSellingModes.SeatBased,
            color: defaultColor,
        },
    })

    const formRows = watch('rows')
    const formColumns = watch('columns')
    const formGalleryRows = watch('galleryRows')
    const formGalleryColumns = watch('galleryColumns')
    const formTotalSeats = watch('totalSeats')

    const { auditorium, gallery, setAuditorium, setGallery, updateSeatsBasedOnDimensions, toggleSeat } = useSeatManagement(
        initialData,
        (count) => {
            if (sourceOfChange.current === 'rowsColumns') {
                setValue('totalSeats', count)
                sourceOfChange.current = null
            }
        },
    )

    const updateRowsAndColumns = useCallback(() => {
        if (defaultSeatSellingMode === SeatSellingModes.SeatBased) {
            const rows = formRows ?? 0
            const columns = formColumns ?? 0
            const galleryRows = formGalleryRows ?? 0
            const galleryColumns = formGalleryColumns ?? 0

            sourceOfChange.current = 'rowsColumns'
            updateSeatsBasedOnDimensions(rows, columns, false)
            updateSeatsBasedOnDimensions(galleryRows, galleryColumns, true)
        }
    }, [defaultSeatSellingMode, formRows, formColumns, formGalleryRows, formGalleryColumns, updateSeatsBasedOnDimensions])

    useEffect(() => {
        if (sourceOfChange.current !== 'totalSeats') {
            updateRowsAndColumns()
        }
    }, [formRows, formColumns, formGalleryRows, formGalleryColumns, updateRowsAndColumns])

    const updateSeatsAutomatically = useCallback(() => {
        if (formTotalSeats === null || formTotalSeats <= 0) return
        if (defaultSeatSellingMode !== SeatSellingModes.General) return

        sourceOfChange.current = 'totalSeats'
        const { rows, columns } = calculateDimensions(formTotalSeats)
        const newSeats = generateSeats(formTotalSeats)
        setValue('rows', rows)
        setValue('columns', columns)
        setAuditorium((prev) => ({ ...prev, rows, columns, seats: newSeats }))
    }, [formTotalSeats, setAuditorium, setValue, defaultSeatSellingMode])

    useEffect(() => {
        if (sourceOfChange.current !== 'rowsColumns') {
            updateSeatsAutomatically()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formTotalSeats])

    useEffect(() => {
        if (defaultSeatSellingMode === SeatSellingModes.General) {
            const auditoriumSeatCount = auditorium?.seats?.length ?? 0
            const gallerySeatCount = gallery?.seats?.length ?? 0
            const currentTotalSeats = auditoriumSeatCount + gallerySeatCount

            sourceOfChange.current = 'rowsColumns'
            setValue('totalSeats', currentTotalSeats)

            updateSeatsAutomatically()
        } else {
            updateRowsAndColumns()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultSeatSellingMode])

    useEffect(() => {
        if (!initialData) return

        const auditoriumSeatCount = initialData.auditorium?.seats?.length || 0
        const gallerySeatCount = initialData.auditorium?.gallery?.seats?.length || 0
        const initialTotalSeats = auditoriumSeatCount + gallerySeatCount

        setValue('totalSeats', initialTotalSeats)
    }, [initialData, setValue]) // empty dependency array to run only on mount

    const onSubmit = async (data: any) => {
        const createLocationDTO: any = {
            location: {
                name: data.locationName,
                address: data.address,
                color: `#${data.color}`,
                default_seat_selling_mode: data.defaultSeatSellingMode,
                auditorium: {
                    auditorium_type: 'location',
                    rows: auditorium?.rows,
                    columns: auditorium?.columns,
                    seats: auditorium?.seats,
                    middle_column: auditoriumMiddleColumn,
                    gallery:
                        gallery?.rows && gallery?.columns
                            ? {
                                  rows: gallery?.rows,
                                  columns: gallery?.columns,
                                  seats: gallery?.seats,
                                  middle_column: galleryMiddleColumn,
                                  gallery_name: data.galleryName || 'Karzat',
                              }
                            : null,
                },
            },
        }

        try {
            if (initialData) {
                await updateLocation(initialData.location_id, createLocationDTO, partnerId)
            } else {
                await createLocation(createLocationDTO)
            }
            onClose && onClose()
        } catch (error) {
            console.error('Error saving location and auditorium:', error)
        }
    }

    const onSubmitEventLocation = async (data: any) => {
        const dto = {
            location: {
                location_id: initialData?.location_id,
                event_id: eventId,
                name: data.locationName,
                address: data.address,
                color: `#${data.color}`,
                default_seat_selling_mode: data.defaultSeatSellingMode,
                auditorium: {
                    auditorium_type: 'event_location',
                    rows: auditorium.rows,
                    columns: auditorium.columns,
                    seats: auditorium.seats,
                    middle_column: auditoriumMiddleColumn,
                    gallery:
                        gallery.rows && gallery.columns
                            ? {
                                  rows: gallery.rows,
                                  columns: gallery.columns,
                                  seats: gallery.seats,
                                  middle_column: galleryMiddleColumn,
                                  gallery_name: data.galleryName || 'Karzat',
                              }
                            : null,
                },
            },
        }

        try {
            const result = await createEventLocation(dto)
            if (result?.event_location_id) {
                lastEventLocationIdRef.current = result.event_location_id
            }
            onClose && onClose()
            return result
        } catch (error) {
            console.error('Error saving location and auditorium:', error)
        }
    }

    const onSubmitEventLocationByDefault = async (data: any) => {
        console.log('onSubmitEventLocationByDefault data', data)

        const dto = {
            location: {
                location_id: initialData?.location_id,
                event_id: eventId,
                name: data.name,
                address: data.address,
                color: data.color,
                default_seat_selling_mode: data.default_seat_selling_mode,
                auditorium: {
                    auditorium_type: 'event_location',
                    rows: data.auditorium?.rows,
                    columns: data.auditorium?.columns,
                    seats: data.auditorium?.seats,
                    middle_column: data.auditorium?.middle_column,
                    gallery:
                        data.auditorium?.gallery?.rows && data.auditorium?.gallery?.columns
                            ? {
                                  rows: data.auditorium.gallery.rows,
                                  columns: data.auditorium.gallery.columns,
                                  seats: data.auditorium.gallery.seats,
                                  middle_column: data.auditorium.gallery.middle_column,
                                  gallery_name: data.auditorium.gallery.gallery_name || 'Karzat',
                              }
                            : null,
                },
            },
        }
        console.log('onSubmitEventLocationByDefault dto', dto)

        try {
            const result = await createEventLocation(dto)
            onClose && onClose()
            return result
        } catch (error) {
            console.error('Error saving location and auditorium:', error)
        }
    }

    return {
        control,
        handleSubmit: handleSubmit(onSubmit),
        handleSubmitEventLocation: handleSubmit(onSubmitEventLocation),
        onSubmitEventLocation,
        onSubmitEventLocationByDefault,
        auditoriumMiddleColumn,
        galleryMiddleColumn,
        setAuditoriumMiddleColumn,
        setGalleryMiddleColumn,
        setValue,
        errors,
        loading: false,
        defaultSeatSellingMode,
        setDefaultSeatSellingMode,
        toggleSeat,
        auditorium,
        setAuditorium,
        gallery,
        setGallery,
        watch,
        lastEventLocationIdRef,
    }
}
